import React, { useEffect, useState } from "react";
import Select from "@/components/Select";
import api from "@/lib/api";

// Nomenclatoarele ReGES (enumerări, județe, naționalități) se încarcă o singură dată per sesiune.
let _nom = null;
export function useRegesNomenclature() {
  const [nom, setNom] = useState(_nom);
  useEffect(() => {
    if (_nom) return;
    api.get("/persons/nomenclatoare").then(r => { _nom = r.data; setNom(r.data); }).catch(() => {});
  }, []);
  return nom;
}

const _loc = {};
function useLocalities(county) {
  const [list, setList] = useState(_loc[county] || []);
  useEffect(() => {
    if (!county) { setList([]); return; }
    if (_loc[county]) { setList(_loc[county]); return; }
    api.get("/persons/localitati", { params: { county } }).then(r => { _loc[county] = r.data; setList(r.data); }).catch(() => setList([]));
  }, [county]);
  return list;
}

function LocalityField({ value, county, onChange, testId, disabled }) {
  const list = useLocalities(county);
  return (
    <Select className="select" value={value || ""} onChange={e => onChange(e.target.value)} data-testid={testId} disabled={disabled || !county}>
      <option value="">{county ? "— alege localitatea —" : "— alege întâi județul —"}</option>
      {value && !list.some(l => l.name === value) && <option value={value}>{value}</option>}
      {list.map(l => <option key={l.siruta} value={l.name}>{l.name}</option>)}
    </Select>
  );
}

/**
 * Câmpurile ReGES ale unei persoane (`kind="person"`) sau ale unui raport de muncă (`kind="employment"`), generate din
 * definiția serverului (/persons/nomenclatoare). `values` = obiectul cu valorile; `onChange(patch)` primește câmpurile schimbate.
 * Fiecare câmp are `data-testid="{prefix}-{cheie}"`.
 */
export default function RegesFields({ kind, values, onChange, prefix, only, keys, disabledKeys }) {
  const nom = useRegesNomenclature();
  if (!nom) return <div style={{ gridColumn: "1/-1", fontSize: 12, color: "#64748B" }}>Se încarcă nomenclatoarele…</div>;
  const all = kind === "person" ? nom.person_fields : nom.employment_fields;
  // `keys` = doar aceste câmpuri, în ordinea dată și fără titlul grupei (formularul le intercalează cu alte câmpuri);
  // `only` = grupele cerute; `disabledKeys` = câmpuri afișate dar blocate
  const fields = keys ? keys.map(k => all.find(f => f.key === k)).filter(Boolean) : all.filter(f => !only || only.includes(f.group));
  const enumOf = (kind === "person" ? nom.person_enum_fields : nom.employment_enum_fields);
  const countyKey = (key) => key.replace("locality", "county");
  const groups = keys ? [null] : [...new Set(fields.map(f => f.group))];
  const off = (key) => !!disabledKeys && disabledKeys.includes(key);
  const set = (key, v) => onChange({ [key]: v });

  const input = (f) => {
    const v = values?.[f.key] ?? "";
    const id = `${prefix}-${f.key}`;
    if (f.type === "enum") {
      return (
        <Select className="select" value={v} onChange={e => set(f.key, e.target.value)} data-testid={id} disabled={off(f.key)}>
          <option value="">—</option>
          {nom.enums[enumOf[f.key]].map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </Select>);
    }
    if (f.type === "date") return <input className="input" type="date" value={v || ""} onChange={e => set(f.key, e.target.value)} data-testid={id} disabled={off(f.key)} />;
    if (f.type === "time") return <input className="input" type="time" value={v || ""} onChange={e => set(f.key, e.target.value)} data-testid={id} />;
    if (f.type === "number") return <input className="input" type="number" step="0.01" min="0" value={v ?? ""} onChange={e => set(f.key, e.target.value === "" ? null : e.target.value)} data-testid={id} />;
    if (f.type === "nationality") {
      return (
        <Select className="select" value={v} onChange={e => set(f.key, e.target.value)} data-testid={id}>
          <option value="">—</option>
          {["România", ...nom.nationalities.filter(n => n !== "România")].map(n => <option key={n} value={n}>{n}</option>)}
        </Select>);
    }
    if (f.type === "county") {
      return (
        <Select className="select" value={v} onChange={e => onChange({ [f.key]: e.target.value, [f.key.replace("county", "locality")]: "" })} data-testid={id}>
          <option value="">—</option>
          {nom.counties.map(c => <option key={c} value={c}>{c}</option>)}
        </Select>);
    }
    if (f.type === "locality") return <LocalityField value={v} county={values?.[countyKey(f.key)]} onChange={x => set(f.key, x)} testId={id} />;
    return <input className="input" value={v || ""} onChange={e => set(f.key, e.target.value)} data-testid={id} maxLength={255} />;
  };

  return (
    <>
      {groups.map(g => (
        <React.Fragment key={g}>
          {g && <div style={{ gridColumn: "1/-1", fontSize: 12, fontWeight: 700, color: "#334155", marginTop: 6 }}>{g}</div>}
          {fields.filter(f => g === null || f.group === g).map(f => (
            <div key={f.key}><label className="label">{f.label}</label>{input(f)}</div>
          ))}
        </React.Fragment>
      ))}
    </>
  );
}
